import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { X, Landmark, Fuel, AlertTriangle, LocateFixed, RefreshCw, Minus, Plus, Truck } from 'lucide-react';
import { useUIStore } from '@/stores/uiStore';
import { useMapStore } from '@/stores/mapStore';
import { useAuthStore } from '@/stores/authStore';
import { useTripRoute } from '@/hooks/useTripRoute';
import { useLivePosition } from '@/hooks/useLivePosition';
import { projectOnPolyline } from '@/utils/routeUtils';
import { formatAresDateTime, getAresWallClockMs } from '@/utils/aresDateTime';
import { getVehicleBrandModel, getVehicleColorMeta, getVehicleTypeLabel } from '@/utils/vehicleIdentity';
import Spinner from '@/components/ui/Spinner';

const ETA_TICK_MS = 30_000;

function formatDurationMinutes(minutes: number | null): string {
  if (minutes == null) return '--';
  const safeMinutes = Math.max(0, Math.round(minutes));
  if (safeMinutes >= 60) {
    return `${Math.floor(safeMinutes / 60)}h ${Math.round(safeMinutes % 60)}m`;
  }
  return `${safeMinutes}m`;
}

export default function RoutePanel() {
  const routePanelOpen = useUIStore((s) => s.routePanelOpen);
  const { routeData, loading, error, showRoute, closeRoute, refreshRoute, currentAlias } = useTripRoute();
  const setHighlightPosition = useMapStore((s) => s.setHighlightPosition);
  const livePosition = useMapStore((s) => s.livePosition);
  const userRole = useAuthStore((s) => s.user?.role);
  const vehicleFeatures = useMapStore((s) => s.vehicleFeatures);
  const lookupAlias = currentAlias || routeData?.alias || null;
  const livePositionForRoute = livePosition?.alias === lookupAlias ? livePosition : null;

  // Deviation callback — recalculates route automatically
  const handleDeviation = useCallback(() => {
    refreshRoute();
  }, [refreshRoute]);

  // Start polling live position with deviation detection
  useLivePosition(currentAlias, handleDeviation);

  const isOffRoute = livePositionForRoute?.off_route === true;
  // Plan progress is origin->destination; live projection is only for the current route segment/POIs.
  const routeProgressPercent = isOffRoute ? null : (routeData?.route_progress_percent ?? null);
  const routeProgressKm = isOffRoute ? null : (routeData?.route_traveled_km ?? null);

  const projection = useMemo(() => {
    if (isOffRoute) return null;
    if (!livePositionForRoute?.lat || !livePositionForRoute?.lon || !routeData?.polyline?.length) return null;
    return projectOnPolyline(livePositionForRoute.lat, livePositionForRoute.lon, routeData.polyline as [number, number][]);
  }, [isOffRoute, livePositionForRoute?.lat, livePositionForRoute?.lon, routeData?.polyline]);

  // Current segment progress: used for POIs ahead and current remaining distance projection.
  const projectionKm = projection ? (projection.totalKm * (projection.progressPercent / 100)) : null;
  const currentSegmentProgressKm = isOffRoute ? null : (livePositionForRoute?.progress_km ?? projectionKm);
  const projectedRemainingKm = projection?.remainingKm ?? (
    !isOffRoute && currentSegmentProgressKm != null && routeData?.distance_km != null
      ? Math.max(0, routeData.distance_km - currentSegmentProgressKm)
      : null
  );

  const [panelHeight, setPanelHeight] = useState(220);
  const [fontSize, setFontSize] = useState(11); // min: 9, max: 14
  const dragging = useRef(false);
  const startY = useRef(0);
  const startH = useRef(0);

  const onDragStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    dragging.current = true;
    startY.current = e.clientY;
    startH.current = panelHeight;

    const onMove = (ev: MouseEvent) => {
      if (!dragging.current) return;
      // drag up = larger (panel anchored at bottom)
      const delta = startY.current - ev.clientY;
      setPanelHeight(Math.min(420, Math.max(140, startH.current + delta)));
    };

    const onUp = () => {
      dragging.current = false;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [panelHeight]);

  // Listen for custom event from popup buttons
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.alias) {
        showRoute(detail.alias);
      }
    };
    window.addEventListener('show-trip-route', handler);
    return () => window.removeEventListener('show-trip-route', handler);
  }, [showRoute]);

  const [etaTickNow, setEtaTickNow] = useState(Date.now());
  useEffect(() => {
    if (!routePanelOpen) return undefined;
    setEtaTickNow(Date.now());
    const intervalId = window.setInterval(() => {
      setEtaTickNow(Date.now());
    }, ETA_TICK_MS);
    return () => window.clearInterval(intervalId);
  }, [
    routePanelOpen,
    currentAlias,
    routeData?.eta_arrival_local,
    routeData?.estimated_arrival,
  ]);

  const matchedVehicle = useMemo(
    () => (lookupAlias ? vehicleFeatures.find((feature) => feature.properties.alias === lookupAlias) ?? null : null),
    [lookupAlias, vehicleFeatures]
  );

  if (!routePanelOpen) return null;

  // Filter POIs: show only ahead if we have progress info
  const allCasetas = routeData?.casetas ?? [];
  const allGasolineras = routeData?.gasolineras ?? [];
  const allRiskAlerts = (routeData?.risk_alerts ?? []).slice().sort((a, b) => (a.km ?? 9999) - (b.km ?? 9999));

  const truckKm = currentSegmentProgressKm;
  const casetas = truckKm != null
    ? allCasetas.filter(c => (c.distance_from_origin_km ?? 0) > truckKm)
    : allCasetas;
  const gasolineras = truckKm != null
    ? allGasolineras.filter(g => (g.distance_from_origin_km ?? 0) > truckKm)
    : allGasolineras;
  const riskAlerts = truckKm != null
    ? allRiskAlerts.filter(a => (a.km ?? 0) > truckKm)
    : allRiskAlerts;

  // Detect if we're loading a different trip (stale routeData)
  const isLoadingNewTrip = loading && currentAlias != null && routeData?.alias !== currentAlias;

  // Customer: prefer route data, fallback to vehicle features (use currentAlias for lookup)
  const customer = routeData?.customer
    || matchedVehicle?.properties.customer
    || null;
  const plate = routeData?.plate
    || matchedVehicle?.properties.plate
    || null;
  const economic = routeData?.economic
    || matchedVehicle?.properties.economic
    || null;
  const vehicleType = getVehicleTypeLabel({
    vehicle_type: routeData?.vehicle_type || matchedVehicle?.properties.vehicle_type || null,
  });
  const vehicleBrandModel = getVehicleBrandModel({
    brand: routeData?.brand || matchedVehicle?.properties.brand || null,
    model: routeData?.model || matchedVehicle?.properties.model || null,
  });
  const vehicleColor = getVehicleColorMeta({
    vehicle_color: routeData?.vehicle_color || matchedVehicle?.properties.vehicle_color || null,
  });

  const dashboardEta = isLoadingNewTrip
    ? null
    : routeData?.eta_arrival_local
      || routeData?.estimated_arrival
      || matchedVehicle?.properties.eta_arrival_local
      || matchedVehicle?.properties.estimated_arrival
      || matchedVehicle?.properties.dashboard_eta_local
      || matchedVehicle?.properties.eta
      || null;

  const etaLabel = formatAresDateTime(dashboardEta);
  const isAtDestination = !isLoadingNewTrip && (
    routeData?.is_at_destination === true
    || matchedVehicle?.properties.is_at_destination === true
  );
  const rawDestinationDistanceM = routeData?.distance_to_destination_m
    ?? matchedVehicle?.properties.distance_to_destination_m
    ?? null;
  const destinationDistanceM = typeof rawDestinationDistanceM === 'number'
    ? rawDestinationDistanceM
    : rawDestinationDistanceM != null
      ? Number(rawDestinationDistanceM)
      : null;
  const distKm = isLoadingNewTrip
    ? null
    : isOffRoute
      ? null
      : (
      routeData?.route_remaining_km
      ?? routeData?.remaining_distance_km
      ?? livePositionForRoute?.route_remaining_km
      ?? projectedRemainingKm
      ?? matchedVehicle?.properties.remaining_distance_km
      ?? null
    );
  const totalDur = isLoadingNewTrip ? null : (routeData?.duration_min ?? null);
  const totalDist = isLoadingNewTrip ? null : (routeData?.distance_km ?? null);
  const routeDurMin = (projectedRemainingKm != null && totalDur != null && totalDist)
    ? totalDur * (projectedRemainingKm / totalDist)
    : totalDur;
  const serverRemainingBaseMin = isLoadingNewTrip ? null : (routeData?.eta_remaining_min ?? null);
  const clientReceivedAtMs = isLoadingNewTrip ? null : (routeData?.client_received_at_ms ?? null);
  const serverDurMin = (serverRemainingBaseMin != null && clientReceivedAtMs != null)
    ? Math.max(0, serverRemainingBaseMin - ((etaTickNow - clientReceivedAtMs) / 60000))
    : null;
  const dashboardEtaMs = getAresWallClockMs(dashboardEta);
  const etaTickNowMs = getAresWallClockMs(new Date(etaTickNow));
  const dashboardDurMin = dashboardEtaMs == null || etaTickNowMs == null
    ? null
    : Math.max(0, (dashboardEtaMs - etaTickNowMs) / 60000);
  const durMin = isAtDestination ? 0 : (dashboardDurMin ?? serverDurMin ?? routeDurMin);
  const distStr = isAtDestination && Number.isFinite(destinationDistanceM)
    ? `${Math.round(destinationDistanceM as number)} m`
    : distKm != null ? `${distKm.toFixed(1)} km` : '--';
  const progressKmStr = routeProgressKm != null ? `${routeProgressKm.toFixed(1)} km` : null;
  const progressPercentStr = routeProgressPercent != null ? `${routeProgressPercent.toFixed(1)}%` : null;
  const durStr = formatDurationMinutes(durMin);

  const centerOnPOI = (lat: number, lon: number, name?: string) => {
    window.dispatchEvent(new CustomEvent('center-map', { detail: { lat, lon, name } }));
  };

  const fitRouteBounds = () => {
    if (routeData?.polyline && routeData.polyline.length > 0) {
      window.dispatchEvent(
        new CustomEvent('fit-route-bounds', { detail: { polyline: routeData.polyline } })
      );
    }
  };

  const highlight = (lat: number, lon: number) => setHighlightPosition([lat, lon]);
  const clearHighlight = () => setHighlightPosition(null);

  const deviationKm = livePositionForRoute?.distance_to_route_m != null
    ? (livePositionForRoute.distance_to_route_m / 1000).toFixed(1)
    : null;

  return (
    <div className="absolute bottom-2.5 right-4 z-[900] w-[680px] max-w-[calc(100%-2rem)] bg-bg-secondary/95 backdrop-blur-xl border border-border-strong rounded-xl shadow-2xl animate-slide-up overflow-hidden">
      {/* Drag handle */}
      <div
        onMouseDown={onDragStart}
        className="flex items-center justify-center h-2 cursor-ns-resize hover:bg-bg-tertiary/50 transition-colors"
      >
        <div className="w-8 h-[3px] rounded-full bg-border-strong" />
      </div>
      {/* Header - Row 1: Alias + Customer + close */}
      <div className="px-3 pt-2 pb-1 flex items-center justify-between">
        <div className="flex items-start gap-2 text-xs min-w-0 flex-1">
          <Truck className="text-accent-blue flex-shrink-0" size={14} />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 text-xs min-w-0 flex-wrap">
              {customer && (
                <>
                  <span className="font-bold text-accent-blue truncate max-w-[220px]">{customer}</span>
                  <span className="text-text-subtle">|</span>
                </>
              )}
              <span className="font-semibold text-text-primary">{currentAlias || routeData?.alias || '...'}</span>
            </div>
            {(plate || economic || vehicleType || vehicleBrandModel || vehicleColor) && (
              <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                {plate && (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md border border-border-subtle bg-bg-tertiary text-[10px] font-medium text-text-secondary">
                    <span className="text-text-muted uppercase tracking-wide">Placa</span>
                    <span className="font-mono text-text-primary">{plate}</span>
                  </span>
                )}
                {economic && (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md border border-border-subtle bg-bg-tertiary text-[10px] font-medium text-text-secondary">
                    <span className="text-text-muted uppercase tracking-wide">Eco</span>
                    <span className="font-mono text-text-primary">{economic}</span>
                  </span>
                )}
                {vehicleType && (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md border border-border-subtle bg-bg-tertiary text-[10px] font-medium text-text-secondary">
                    <span className="text-text-muted uppercase tracking-wide">Tipo</span>
                    <span className="text-text-primary">{vehicleType}</span>
                  </span>
                )}
                {vehicleBrandModel && (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md border border-border-subtle bg-bg-tertiary text-[10px] font-medium text-text-secondary">
                    <span className="text-text-muted uppercase tracking-wide">Unidad</span>
                    <span className="text-text-primary">{vehicleBrandModel}</span>
                  </span>
                )}
                {vehicleColor && (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md border border-border-subtle bg-bg-tertiary text-[10px] font-medium text-text-secondary">
                    {vehicleColor.swatch && (
                      <span
                        className="h-2 w-2 rounded-full border border-white/20"
                        style={{ backgroundColor: vehicleColor.swatch }}
                      />
                    )}
                    <span className="text-text-muted uppercase tracking-wide">Color</span>
                    <span className="text-text-primary">{vehicleColor.label}</span>
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {/* Font size controls */}
          <div className="flex items-center gap-0.5 bg-bg-tertiary rounded border border-border-subtle">
            <button
              onClick={() => setFontSize((s) => Math.max(9, s - 1))}
              disabled={fontSize <= 9}
              className="w-5 h-5 flex items-center justify-center text-text-muted hover:text-text-primary disabled:opacity-30 disabled:cursor-not-allowed transition-all"
              title="Reducir texto"
            >
              <span className="text-[9px] font-bold">A</span>
              <Minus size={7} className="-ml-0.5" />
            </button>
            <div className="w-px h-3 bg-border-subtle" />
            <button
              onClick={() => setFontSize((s) => Math.min(14, s + 1))}
              disabled={fontSize >= 14}
              className="w-5 h-5 flex items-center justify-center text-text-muted hover:text-text-primary disabled:opacity-30 disabled:cursor-not-allowed transition-all"
              title="Aumentar texto"
            >
              <span className="text-[10px] font-bold">A</span>
              <Plus size={7} className="-ml-0.5" />
            </button>
          </div>
          <button
            onClick={refreshRoute}
            disabled={loading}
            className="w-6 h-6 rounded bg-bg-tertiary border border-border-subtle text-text-muted hover:text-accent-blue hover:border-accent-blue disabled:opacity-50 flex items-center justify-center transition-all"
            title="Recalcular ruta desde posición actual"
          >
            <RefreshCw size={10} className={loading ? 'animate-spin' : ''} />
          </button>
          {routeData?.polyline && (
            <button
              onClick={fitRouteBounds}
              className="w-6 h-6 rounded bg-bg-tertiary border border-border-subtle text-text-muted hover:text-accent-blue hover:border-accent-blue flex items-center justify-center transition-all"
              title="Centrar en ruta"
            >
              <LocateFixed size={11} />
            </button>
          )}
          <button
            onClick={closeRoute}
            className="w-6 h-6 rounded bg-bg-tertiary border border-border-subtle text-text-muted hover:text-text-primary flex items-center justify-center transition-all"
          >
            <X size={12} />
          </button>
        </div>
      </div>
      {/* Header - Row 2: Route stats */}
      <div className="px-3 pb-2 border-b border-border-subtle flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px]">
        <div className="flex items-center gap-1.5">
          <span className="text-text-muted">Dist. a destino:</span>
          <span className="font-mono font-bold text-text-primary">{distStr}</span>
        </div>
        {progressKmStr && (
          <div className="flex items-center gap-1.5">
            <span className="text-text-muted">Recorrido:</span>
            <span className="font-mono font-bold text-text-primary">{progressKmStr}</span>
          </div>
        )}
        {progressPercentStr && (
          <div className="flex items-center gap-1.5">
            <span className="text-text-muted">Avance:</span>
            <span className="font-mono font-bold text-accent-blue">{progressPercentStr}</span>
          </div>
        )}
        <div className="flex items-center gap-1.5">
          <span className="text-text-muted">ETA:</span>
          <span className="font-mono font-bold text-text-primary">{isAtDestination ? 'Llegó' : etaLabel.time}</span>
          {!isAtDestination && <span className="text-text-subtle">{etaLabel.date}</span>}
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-text-muted">Tiempo restante:</span>
          <span className="font-mono font-bold text-text-primary">{durStr}</span>
        </div>
        {userRole === 'admin' && livePositionForRoute?.speed != null && (
          <div className="flex items-center gap-1.5">
            <span className="text-text-muted">Vel:</span>
            <span className="font-mono font-bold text-accent-blue">{Math.round(livePositionForRoute.speed)} km/h</span>
          </div>
        )}
        {userRole === 'admin' && livePositionForRoute?.last_event && (
          <div className="flex items-center gap-1.5">
            <span className="text-text-muted">GPS:</span>
            <span className="font-mono text-text-subtle">
              {(() => {
                try {
                  return formatAresDateTime(livePositionForRoute.last_event).time;
                } catch { return '--'; }
              })()}
            </span>
          </div>
        )}
      </div>

      {/* Deviation banner */}
      {isOffRoute && !loading && (
        <div className="px-3 py-1.5 border-b border-accent-red/30 bg-accent-red/10 flex items-center gap-2 text-[10px] text-accent-red">
          <AlertTriangle className="flex-shrink-0" size={12} />
          <span className="font-semibold">
            Fuera de ruta{deviationKm ? ` (${deviationKm} km)` : ''}
          </span>
          <span className="text-accent-red/70">— Recalculando automáticamente...</span>
        </div>
      )}

      {/* Timestamp from last route calculation removed — speed+GPS now in header */}

      {/* Body */}
      {loading && (
        <div className="flex items-center justify-center py-5 gap-2">
          <Spinner size="sm" />
          <span className="text-xs text-text-muted">Calculando ruta...</span>
        </div>
      )}

      {error && (
        <div className="text-center py-4 text-accent-red text-xs">{error}</div>
      )}

      {routeData && !loading && (
        <div className="grid divide-x divide-border-subtle" style={{ gridTemplateColumns: '1fr 1fr 1.8fr', height: `${panelHeight}px`, fontSize: `${fontSize}px` }}>
          {/* Casetas */}
          <div className="flex flex-col min-w-0 overflow-hidden">
            <div className="px-2.5 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-text-muted flex items-center gap-1.5 border-b border-border-subtle flex-shrink-0">
              <Landmark className="text-accent-green" size={10} />
              Casetas
              <span className="ml-auto bg-bg-tertiary px-1.5 py-0.5 rounded text-[9px] font-mono">
                {casetas.length}{truckKm != null && allCasetas.length !== casetas.length ? `/${allCasetas.length}` : ''}
              </span>
            </div>
            <div className="flex-1 overflow-y-auto min-h-0">
              {casetas.length === 0 ? (
                <p className="text-text-subtle px-2.5 py-2" style={{ fontSize: '0.9em' }}>
                  {truckKm != null ? 'Sin casetas adelante' : 'Sin casetas'}
                </p>
              ) : (
                casetas.map((c, i) => (
                  <button
                    key={i}
                    className="w-full text-left flex items-center gap-1.5 px-2.5 py-[3px] text-text-secondary hover:bg-bg-tertiary transition-colors border-b border-border-subtle/30 cursor-pointer"
                    onClick={() => centerOnPOI(c.lat, c.lon, c.name)}
                    onMouseEnter={() => highlight(c.lat, c.lon)}
                    onMouseLeave={clearHighlight}
                  >
                    <span className="text-accent-green flex-shrink-0 w-3 text-center" style={{ fontSize: '0.8em' }}>●</span>
                    <span className="flex-1 truncate">{c.name}</span>
                    {c.distance_from_origin_km != null && (
                      <span className="flex-shrink-0 text-text-subtle font-mono" style={{ fontSize: '0.9em' }}>
                        {truckKm != null
                          ? `${Math.round(c.distance_from_origin_km - truckKm)} km`
                          : `${Math.round(c.distance_from_origin_km)} km`
                        }
                      </span>
                    )}
                  </button>
                ))
              )}
            </div>
          </div>

          {/* Gasolineras */}
          <div className="flex flex-col min-w-0 overflow-hidden">
            <div className="px-2.5 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-text-muted flex items-center gap-1.5 border-b border-border-subtle flex-shrink-0">
              <Fuel className="text-accent-orange" size={10} />
              Gasolineras
              <span className="ml-auto bg-bg-tertiary px-1.5 py-0.5 rounded text-[9px] font-mono">
                {gasolineras.length}{truckKm != null && allGasolineras.length !== gasolineras.length ? `/${allGasolineras.length}` : ''}
              </span>
            </div>
            <div className="flex-1 overflow-y-auto min-h-0">
              {gasolineras.length === 0 ? (
                <p className="text-text-subtle px-2.5 py-2" style={{ fontSize: '0.9em' }}>
                  {truckKm != null ? 'Sin gasolineras adelante' : 'Sin gasolineras'}
                </p>
              ) : (
                gasolineras.slice(0, 30).map((g, i) => (
                  <button
                    key={i}
                    className="w-full text-left flex items-center gap-1.5 px-2.5 py-[3px] text-text-secondary hover:bg-bg-tertiary transition-colors border-b border-border-subtle/30 cursor-pointer"
                    onClick={() => centerOnPOI(g.lat, g.lon, g.name)}
                    onMouseEnter={() => highlight(g.lat, g.lon)}
                    onMouseLeave={clearHighlight}
                  >
                    <span className="text-accent-orange flex-shrink-0 w-3 text-center" style={{ fontSize: '0.8em' }}>●</span>
                    <span className="flex-1 truncate">{g.name}</span>
                    {g.distance_from_origin_km != null && (
                      <span className="flex-shrink-0 text-text-subtle font-mono" style={{ fontSize: '0.9em' }}>
                        {truckKm != null
                          ? `${Math.round(g.distance_from_origin_km - truckKm)} km`
                          : `${Math.round(g.distance_from_origin_km)} km`
                        }
                      </span>
                    )}
                  </button>
                ))
              )}
              {gasolineras.length > 30 && (
                <p className="text-text-subtle text-center py-1" style={{ fontSize: '0.8em' }}>
                  +{gasolineras.length - 30} más
                </p>
              )}
            </div>
          </div>

          {/* Alertas de Riesgo */}
          <div className="flex flex-col min-w-0 overflow-hidden">
            <div className="px-2.5 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-text-muted flex items-center gap-1.5 border-b border-border-subtle flex-shrink-0">
              <AlertTriangle className="text-accent-red" size={10} />
              Riesgos Futuros
              <span className="ml-auto bg-bg-tertiary px-1.5 py-0.5 rounded text-[9px] font-mono">
                {riskAlerts.length}{truckKm != null && allRiskAlerts.length !== riskAlerts.length ? `/${allRiskAlerts.length}` : ''}
              </span>
            </div>
            <div className="flex-1 overflow-y-auto min-h-0">
              {riskAlerts.length === 0 ? (
                <p className="text-text-subtle px-2.5 py-2" style={{ fontSize: '0.9em' }}>Sin alertas</p>
              ) : (
                riskAlerts.map((a, i) => {
                  const levelColor =
                    a.level === 'critical' ? '#ef4444' :
                    a.level === 'alert' ? '#f59e0b' :
                    a.level === 'warning' ? '#eab308' :
                    '#3b82f6';
                  return (
                    <button
                      key={i}
                      className="w-full text-left flex items-start gap-1.5 px-2.5 py-1 hover:bg-bg-tertiary transition-colors border-b border-border-subtle/30 cursor-pointer"
                      style={{ borderLeft: `3px solid ${levelColor}` }}
                      onClick={() => {
                        if (a.lat != null && a.lon != null) {
                          centerOnPOI(a.lat, a.lon, a.zone_name || a.message);
                        }
                      }}
                      onMouseEnter={() => {
                        if (a.lat != null && a.lon != null) highlight(a.lat, a.lon);
                      }}
                      onMouseLeave={clearHighlight}
                    >
                      <AlertTriangle
                        className="flex-shrink-0 mt-0.5"
                        style={{ color: levelColor, width: '0.9em', height: '0.9em' }}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-text-secondary leading-snug break-words">
                          {a.message || a.zone_name}
                        </div>
                        <div className="text-text-subtle" style={{ fontSize: '0.8em' }}>
                          {a.zone_name && <span>{a.zone_name}</span>}
                          {a.km != null && (
                            <span>
                              {a.zone_name ? ' · ' : ''}
                              {truckKm != null ? `en ${Math.round(a.km - truckKm)} km` : `${a.km} km`}
                            </span>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
